import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Card, Table, Skeleton, Empty, Input, Button, Pagination } from 'antd';
import {
  FundOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { ThemeContext } from '../../context/ThemeContext';
import LandingLayout from './LandingLayout';
import './IssuerCategory.css';
import './Instruments.css';
import './FixedDepositPage.css';
import apiClient from '../../services/apiClient';

// 9 separate light colours — darker ones softened, already-light ones unchanged
const FD_CATEGORY_COLORS = {
  'SBI & Nationalised Banks': '#eff6ff',
  'Domestic Private Sector Banks': '#ecfdf5',
  'Small Finance Banks': '#fef3c7',
  'Payments Banks': '#f3f0ff',
  'Regional Rural Banks': '#e8faf0',
  'Foreign banks in India': '#fdf2f8',
  'NBFC': '#fefce8',
  'Financial Institutional': '#e6faf6',
  Other: '#f3f4f6',
};

const FD_ALL_COLOR = '#f3f4f6';

const getCategoryColor = (category) => {
  const key = category || 'Other';
  return FD_CATEGORY_COLORS[key] || FD_CATEGORY_COLORS.Other;
};

const getCardBackgroundColor = (selectedCategory, companyCategory) => {
  if (!selectedCategory) {
    return FD_ALL_COLOR;
  }
  return getCategoryColor(selectedCategory || companyCategory);
};

const getCompanyInitials = (companyName) => {
  const name = companyName || '';
  if (name.includes('HDFC')) return 'HDFC';
  if (name.includes('REC')) return 'REC';
  if (name.includes('SBI')) return 'SBI';
  if (name.includes('ICICI')) return 'ICICI';
  if (name.includes('AXIS')) return 'AXIS';
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word.charAt(0)).join('');
  }
  return name.substring(0, 3).toUpperCase();
};

const formatInterestRateDisplay = (rate) => {
  if (!rate || rate === '—') return { value: '—', suffix: '' };
  const numeric = rate.toString().replace(/[^\d.]/g, '');
  if (!numeric) return { value: rate, suffix: '' };
  const num = parseFloat(numeric);
  if (!Number.isFinite(num)) return { value: rate, suffix: '' };
  const value = `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}%`;
  return { value, suffix: 'P.A.' };
};

const FD_AGENCY_LABELS = {
  crisil: 'CRISIL',
  care: 'CARE',
  icra: 'ICRA',
  india: 'INDIA',
  fitch: 'FITCH',
  sp: 'S&P',
};

const cleanRatingText = (rating) => {
  if (!rating) return '';
  return String(rating).replace(/\s+/g, ' ').trim();
};

/** Flatten API ratings map → [{ agency, rating }] for card/detail UI. */
const toFdRatingRows = (ratings) => {
  if (!ratings || typeof ratings !== 'object') return [];
  return Object.entries(ratings)
    .filter(([, summary]) => summary && summary.highestRating)
    .map(([agency, summary]) => ({
      key: agency,
      agency: FD_AGENCY_LABELS[agency] || String(agency).toUpperCase(),
      rating: cleanRatingText(summary.highestRating),
    }))
    .filter((row) => row.rating);
};

const FD_TABLE_COLUMNS = [
  {
    title: 'Tenure',
    dataIndex: 'tenure',
    key: 'tenure',
    render: (value) => value || 'N/A',
  },
  {
    title: 'Interest Rate',
    dataIndex: 'interestRate',
    key: 'interestRate',
    render: (value) => value || '0%',
  },
];

const FD_PAGE_SIZE_DEFAULT = 12;

// `value` is sent to the API and must match the stored bank_category in the DB.
// `label` is what we show on the chip (kept clean for display).
const FD_CATEGORIES = [
  { value: 'SBI & Nationalised Banks', label: 'SBI & Nationalised Banks' },
  { value: 'Domestic Private Sector Banks', label: 'Domestic Private Sector Banks' },
  { value: 'Small Finance Banks', label: 'Small Finance Banks' },
  { value: 'Payments Banks', label: 'Payments Banks' },
  { value: 'Regional Rural Banks', label: 'Regional Rural Banks' },
  { value: 'Foreign banks in India', label: 'Foreign banks in India' },
  { value: 'NBFC', label: 'NBFC' },
  { value: 'Financial Institutional', label: 'Financial Institutional' },
  { value: 'Other', label: 'Other' },
];

const getCategoryLabel = (category) => category || 'Other';

const FixedDepositPage = () => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { companyName: companyNameParam } = useParams();
  const [companies, setCompanies] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalRates, setTotalRates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(FD_PAGE_SIZE_DEFAULT);
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  const selectedCompany = useMemo(() => {
    if (!companyNameParam) return null;
    try {
      return decodeURIComponent(companyNameParam);
    } catch {
      return companyNameParam;
    }
  }, [companyNameParam]);

  useEffect(() => {
    if (selectedCompany) return undefined;

    let isMounted = true;

    const fetchCompanies = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(currentPage - 1),
          size: String(pageSize),
        });
        const trimmedSearch = searchTerm.trim();
        if (trimmedSearch) {
          params.set('search', trimmedSearch);
        }
        if (selectedCategory) {
          params.set('category', selectedCategory);
        }

        const res = await apiClient.get(`${baseUrl}/fixed-deposits/all?${params.toString()}`);
        const json = res.data;
        const payload = json?.data;

        if (!payload || !Array.isArray(payload.content)) {
          if (res.status === 204 || json?.statusCode === 204) {
            if (isMounted) {
              setCompanies([]);
              setTotalCompanies(0);
              setTotalRates(0);
            }
            return;
          }
          throw new Error('Unexpected fixed deposit response format');
        }

        if (isMounted) {
          setCompanies(payload.content);
          setTotalCompanies(payload.totalElements ?? payload.content.length);
          setTotalRates(payload.totalRates ?? 0);
        }
      } catch (err) {
        if (isMounted) {
          if (err?.response?.status === 204) {
            setCompanies([]);
            setTotalCompanies(0);
            setTotalRates(0);
          } else {
            setError(err.message);
            setCompanies([]);
            setTotalCompanies(0);
            setTotalRates(0);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanies();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, selectedCompany, currentPage, pageSize, searchTerm, selectedCategory]);

  useEffect(() => {
    if (!selectedCompany) {
      setSelectedCompanyData(null);
      setDetailError(null);
      return undefined;
    }

    let isMounted = true;

    const fetchCompanyRates = async () => {
      setDetailLoading(true);
      setDetailError(null);

      try {
        const res = await apiClient.get(
          `${baseUrl}/fixed-deposits/issuer/${encodeURIComponent(selectedCompany)}`
        );
        const json = res.data;
        const content = json?.data?.content ?? [];

        if (!Array.isArray(content) || content.length === 0) {
          if (isMounted) {
            setSelectedCompanyData(null);
          }
          return;
        }

        if (isMounted) {
          setSelectedCompanyData({
            companyName: selectedCompany,
            rateCount: content.length,
            items: content,
          });
        }
      } catch (err) {
        if (isMounted) {
          setSelectedCompanyData(null);
          setDetailError('Error fetching fixed deposit rates');
        }
      } finally {
        if (isMounted) {
          setDetailLoading(false);
        }
      }
    };

    fetchCompanyRates();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, selectedCompany]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCompany, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const handleCompanySelect = (companyName) => {
    setSearchTerm('');
    navigate(`/fixed-deposits/${encodeURIComponent(companyName)}`);
  };

  const handleBackToList = () => {
    navigate('/fixed-deposits');
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handlePageSizeChange = (_current, size) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  return (
    <LandingLayout>
      <div className={`bond-categories-page fixed-deposit-page ${isDark ? 'dark' : ''}`}>
        <div className="container">
          <header className="issuer-categorization-header">
            <h1 className="issuer-categorization-title">
              <FundOutlined className="issuer-categorization-icon" />
              Fixed Deposits
            </h1>
            <p className="issuer-categorization-hint">
              {selectedCompany
                ? `Rates offered by ${selectedCompany}`
                : 'Select a company to view its fixed deposit rates'}
            </p>
          </header>

          <div className="fixed-deposit-toolbar">
            {selectedCompany ? (
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToList}
                className="fixed-deposit-back-btn"
              >
                All Companies
              </Button>
            ) : (
              <div className="search-filter-bar fixed-deposit-search-bar">
                <div className="search-input-container">
                  <Input
                    allowClear
                    placeholder="Search by company name..."
                    prefix={<SearchOutlined className="search-input-icon" />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="isin-search-input"
                  />
                </div>
              </div>
            )}
            {!loading && !error && !selectedCompany && totalCompanies > 0 && (
              <div className="fixed-deposit-stats">
                <span>{totalCompanies} companies</span>
                <span>{totalRates} rates</span>
              </div>
            )}
          </div>

          {!selectedCompany && (
            <div className="fixed-deposit-category-filter" aria-label="Fixed deposit categories">
              <button
                type="button"
                className={`fixed-deposit-category-chip ${selectedCategory === '' ? 'active fd-chip--category' : ''}`}
                style={{ '--fd-chip-color': FD_ALL_COLOR }}
                onClick={() => setSelectedCategory('')}
              >
                All
              </button>
              {FD_CATEGORIES.map((category) => {
                const isActive = selectedCategory === category.value;
                return (
                <button
                  type="button"
                  key={category.value}
                  className={`fixed-deposit-category-chip ${isActive ? 'active fd-chip--category' : ''}`}
                  style={{ '--fd-chip-color': getCategoryColor(category.value) }}
                  onClick={() => setSelectedCategory(category.value)}
                >
                  {category.label}
                </button>
              );
              })}
            </div>
          )}

          {!selectedCompany && loading && (
            <Card className="fd-state-card loading-card">
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          )}

          {!selectedCompany && !loading && error && (
            <Card className="fd-state-card">
              <p className="fd-error">{error}</p>
            </Card>
          )}

          {!selectedCompany && !loading && !error && !companies.length && (
            <Card className="fd-state-card">
              <Empty description={searchTerm.trim() ? 'No companies match your search' : 'No fixed deposit rates available'} />
            </Card>
          )}

          {!selectedCompany && !loading && !error && companies.length > 0 && (
            <>
              <Row gutter={[16, 16]} className="issuers-grid">
                {companies.map((company) => {
                  const backgroundColor = getCardBackgroundColor(
                    selectedCategory,
                    company.bankCategory
                  );
                  const rateDisplay = formatInterestRateDisplay(company.interestRate);
                  const ratingRows = toFdRatingRows(company.ratings);
                  return (
                    <Col xs={24} sm={12} lg={8} key={company.companyName}>
                      <Card
                        className="bond-card fd-company-card"
                        hoverable
                        onClick={() => handleCompanySelect(company.companyName)}
                        style={{
                          borderRadius: '16px',
                          backgroundColor,
                          '--card-bg-color': backgroundColor,
                          cursor: 'pointer',
                        }}
                      >
                        <div className="bond-card-inner">
                          <div className="issuer-header-section">
                            <div className="issuer-name-row">
                              <div className="company-logo">
                                <div className="company-logo-text">
                                  {getCompanyInitials(company.companyName)}
                                </div>
                              </div>
                              <div className="issuer-company-name">
                                {company.companyName}
                              </div>
                            </div>
                            {!selectedCategory && (
                              <span className="fd-category-badge">
                                {getCategoryLabel(company.bankCategory)}
                              </span>
                            )}
                          </div>

                          <div className={`fd-card-meta-row${ratingRows.length > 0 ? '' : ' fd-card-meta-row--tenure-only'}`}>
                            <div className="fd-card-grid">
                              <div className="fd-card-cell fd-card-cell--tenure">
                                <span className="fd-card-cell-label">Tenure</span>
                                <span className="fd-card-cell-value">{company.tenure || '—'}</span>
                              </div>
                            </div>

                            {ratingRows.length > 0 && (
                              <div className="fd-card-ratings">
                                <span className="fd-card-ratings-label">FD Rating</span>
                                <div className="fd-card-ratings-list">
                                  {ratingRows.slice(0, 2).map((row) => (
                                    <span className="fd-rating-chip" key={row.key}>
                                      <span className="fd-rating-chip-value">{row.rating}</span>
                                      <span className="fd-rating-chip-agency">{row.agency}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="fd-card-footer">
                            <div className="fd-card-yield">
                              <span className="fd-card-yield-label">Interest Rate</span>
                              <div className="fd-card-yield-row">
                                <span className="fd-card-yield-pct">{rateDisplay.value}</span>
                                {rateDisplay.suffix ? (
                                  <span className="fd-card-yield-pa">{rateDisplay.suffix}</span>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="fd-card-cta"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCompanySelect(company.companyName);
                              }}
                            >
                              View Details
                              <RightOutlined className="fd-card-cta-icon" />
                            </button>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {totalCompanies > pageSize && (
                <div className="pagination-container fd-pagination-container">
                  <Pagination
                    current={currentPage}
                    total={totalCompanies}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showSizeChanger
                    onShowSizeChange={handlePageSizeChange}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} companies`}
                    pageSizeOptions={['12', '24', '48', '96']}
                  />
                </div>
              )}
            </>
          )}

          {selectedCompany && detailLoading && (
            <Card className="fd-state-card loading-card">
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          )}

          {selectedCompany && !detailLoading && detailError && (
            <Card className="fd-state-card">
              <p className="fd-error">{detailError}</p>
            </Card>
          )}

          {selectedCompany && !detailLoading && !detailError && selectedCompanyData && (
            <Card className="fd-detail-card" bordered={false}>
              <div className="fd-detail-header">
                <div className="company-logo fd-detail-logo">
                  <div className="company-logo-text">
                    {getCompanyInitials(selectedCompanyData.companyName)}
                  </div>
                </div>
                <div>
                  <h3 className="fd-detail-company-name">{selectedCompanyData.companyName}</h3>
                  <p className="fd-detail-subtitle">
                    {selectedCompanyData.rateCount} fixed deposit{' '}
                    {selectedCompanyData.rateCount === 1 ? 'rate' : 'rates'}
                    {selectedCompanyData.items?.[0]?.bankCategory
                      ? ` - ${selectedCompanyData.items[0].bankCategory}`
                      : ''}
                  </p>
                  {(() => {
                    const detailRatings = toFdRatingRows(selectedCompanyData.items?.[0]?.ratings);
                    if (!detailRatings.length) return null;
                    return (
                      <div className="fd-detail-ratings">
                        <span className="fd-detail-ratings-label">FD Ratings</span>
                        <div className="fd-card-ratings-list">
                          {detailRatings.map((row) => (
                            <span className="fd-rating-chip" key={row.key}>
                              <span className="fd-rating-chip-value">{row.rating}</span>
                              <span className="fd-rating-chip-agency">{row.agency}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <Table
                className="fd-detail-table"
                pagination={false}
                size="middle"
                dataSource={selectedCompanyData.items.map((item, index) => ({
                  ...item,
                  key: `${selectedCompanyData.companyName}-${index}`,
                }))}
                columns={FD_TABLE_COLUMNS}
              />
            </Card>
          )}

          {selectedCompany && !detailLoading && !detailError && !selectedCompanyData && (
            <Card className="fd-state-card">
              <Empty description="Company not found">
                <Button type="primary" onClick={handleBackToList}>
                  Back to all companies
                </Button>
              </Empty>
            </Card>
          )}
        </div>
      </div>
    </LandingLayout>
  );
};

export default FixedDepositPage;
